import type { DuplicateCandidate, DuplicateSignal } from '@sentra/contracts';
import { UserRound, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { PersonThumbnail } from '@/features/persons/images/person-thumbnail';

/**
 * The as-you-type duplicate guard (AD-18): records that may already be the person at the desk.
 *
 * **It warns and never blocks.** Blocking a registration because a passport number collides is the
 * exact failure AD-18 exists to prevent: two people can share a document number through a data-entry
 * error, a reissued passport legitimately repeats, and a family shares a surname. What a block
 * produces is an operator who cannot register somebody standing in front of them; what a warning
 * produces is an operator who looks. The dismiss control is there so a warning that has been read
 * stops taking up the screen, and dismissing it changes nothing about what can be submitted.
 *
 * **The photograph is the point.** A short reference and a status cannot tell an operator whether
 * the record on screen is the person in front of them, and a name is not returned by this route at
 * all — the candidate carries ids, a status, a presence state and the signals that matched, and
 * nothing decrypted was needed to produce it. The thumbnails come from the batched read, so every
 * candidate's face is one request between them, and a candidate the operator saw a moment ago is
 * drawn from memory rather than asked for again each time the check re-runs as they type.
 *
 * **`matchedOn` is shown, because a record matching MORE THAN ONE signal is a stronger warning**
 * than one matching a name fingerprint alone, and collapsing them would hide that.
 */
/**
 * The sentence each signal is shown as.
 *
 * **A total record rather than a ternary, and that is the fix rather than a tidy-up.** This was two
 * branches — passport, else name — so the day a third signal arrived it would have rendered as "the
 * name matches" for a record found by its reference code: a wrong explanation of a correct warning,
 * with nothing failing anywhere. Keyed by the contract's own union, so a fourth signal is a compile
 * error here.
 */
const SIGNAL_LABEL = {
  fingerprint: 'person.form.matchedName',
  passport: 'person.form.matchedPassport',
  reference: 'person.form.matchedReference',
} as const satisfies Readonly<Record<DuplicateSignal, string>>;

export function DuplicateGuard({
  candidates,
  onDismiss,
  onOpen,
}: Readonly<{
  candidates: readonly DuplicateCandidate[];
  onDismiss: () => void;
  /** Opening one is a record action and goes through the caller's acknowledgment (AD-53). */
  onOpen?: (candidate: DuplicateCandidate) => void;
}>): ReactNode {
  const { t } = useTranslation();

  if (candidates.length === 0) {
    return null;
  }

  return (
    <Alert role="status" data-testid="duplicate-guard">
      <UserRound aria-hidden />
      <AlertTitle>{t('person.form.duplicateTitle', { count: candidates.length })}</AlertTitle>
      <AlertDescription>
        <p>{t('person.form.duplicateBody')}</p>
        <ul className="mt-2 flex w-full flex-col gap-2">
          {candidates.map((candidate) => (
            <li
              key={candidate.personId}
              className="flex items-center gap-3 rounded-md border p-2"
              data-duplicate={candidate.shortRef}
            >
              {candidate.primaryImageId === null ? (
                <span
                  aria-hidden
                  className="flex size-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                >
                  <UserRound />
                </span>
              ) : (
                <PersonThumbnail
                  imageId={candidate.primaryImageId}
                  alt={t('person.form.duplicatePhotoAlt', { shortRef: candidate.shortRef })}
                  className="size-12 shrink-0 rounded-md object-cover"
                />
              )}
              <span className="flex flex-col">
                <span className="font-mono text-sm" data-numeric>
                  {candidate.shortRef}
                </span>
                <span className="text-xs text-muted-foreground">
                  {candidate.matchedOn.map((signal) => t(SIGNAL_LABEL[signal])).join(' · ')}
                </span>
              </span>
              {onOpen !== undefined && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => {
                    onOpen(candidate);
                  }}
                >
                  {t('person.form.duplicateOpen')}
                </Button>
              )}
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={onDismiss}
          data-testid="duplicate-dismiss"
        >
          <X aria-hidden />
          {t('person.form.duplicateDismiss')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
